import React from "react";
import { Placeholder } from "rsuite";
import PropTypes from "prop-types";
import _ from "lodash";

export const ButtonHOC = (WrappedComponent: any, stylesFn: Function) => {
  const Comp = ({
    text = "",
    revert = false,
    ghost = false,
    empty = false,
    size = "medium",
    customStyles = {},
    bold = false,
    icon = "",
    disabled = false,
    loading = false,
    type = "button",
    coloredBtn = false,
    showIcon = false,
    onClick = () => {},
    ...rest
  }) => {
    const classes = stylesFn({
      bold,
      empty,
      size,
      coloredBtn,
      customStyles,
      showIcon,
      hasText: text.length > 0,
    });
    return ghost ? (
      <Placeholder.Graph active className={classes.placeholder} style={{ maxHeight: "3rem" }} />
    ) : (
      <WrappedComponent
        classes={classes}
        text={text} // Pass text prop explicitly
        revert={revert}
        ghost={ghost}
        empty={empty}
        size={size}
        type={type}
        icon={icon}
        coloredBtn={coloredBtn}
        loading={loading}
        disabled={disabled}
        customStyles={customStyles}
        showIcon={showIcon}
        onClick={onClick}
        {...rest}
      />
    );
  };
  Comp.defaultProps = {
    ghost: false,
    text: "",
    revert: false,
    showIcon: false,
    customStyles: {},
    empty: false,
    type: "button",
    disabled: false,
    coloredBtn: false,
    icon: "",
    loading: false,
    size: "medium",
  };
  Comp.propTypes = {
    text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    revert: PropTypes.bool,
    ghost: PropTypes.bool,
    empty: PropTypes.bool,
    icon: PropTypes.string,
    coloredBtn: PropTypes.bool,
    size: PropTypes.string,
    disabled: PropTypes.bool,
    customStyles: PropTypes.object,
    showIcon: PropTypes.bool,
    loading: PropTypes.bool,
    type: PropTypes.string,
    bold: PropTypes.bool,
    onClick: PropTypes.func,
  };

  return Comp;
};

export const DefaultMSFNavBtn = ({
  type = "button",
  id = "",
  classes = { button: "" },
  disabled = false,
  children,
  onClick = () => {},
}: any) => {
  return (
    <button
      id={id}
      className={classes?.button || ""}
      onClick={onClick}
      type={type as "button" | "submit" | "reset"}
      disabled={disabled}
    >
      {children}
    </button>
  );
};
DefaultMSFNavBtn.propTypes = {
  type: PropTypes.string,
  disabled: PropTypes.bool,
  classes: PropTypes.object,
  children: PropTypes.node,
  onClick: PropTypes.func,
  id: PropTypes.string,
};

type DefaultBtnProps = {
  id?: string;
  revert?: boolean;
  type?: "button" | "submit" | "reset";
  classes: {
    ButtonDefault: string;
    ButtonReverted: string;
  };
  title: string | null;
  disabled?: boolean;
  children: React.ReactNode[];
  onClick: () => void;
};
export function DefaultBtn({
  id,
  revert = false,
  type = "button",
  classes = { ButtonDefault: "", ButtonReverted: "" },
  disabled = false,
  title = null,
  children,
  onClick,
}: DefaultBtnProps) {
  const className = revert ? classes.ButtonReverted : classes.ButtonDefault;

  return (
    <button
      id={id}
      title={title ?? ""}
      className={`${className} grid grid-flow-col justify-center items-center`}
      onClick={_.debounce(onClick, 1000, {
        leading: true,
        trailing: false,
      })}
      type={type}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
DefaultBtn.propTypes = {
  id: PropTypes.string,
  revert: PropTypes.bool,
  type: PropTypes.string,
  classes: PropTypes.shape({
    ButtonDefault: PropTypes.string.isRequired,
    ButtonReverted: PropTypes.string.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

export function DefaultSearchBtn({
  classes = { SearchButton: "" },
  id = "",
  children = [],
  onClick = () => {},
}) {
  return (
    <button
      id={id}
      className={`${classes.SearchButton} grid grid-flow-col justify-center items-center`}
      onClick={onClick}
      type="submit"
    >
      {children}
    </button>
  );
}
DefaultSearchBtn.propTypes = {
  classes: PropTypes.shape({
    SearchButton: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  id: PropTypes.string,
};
